if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.querySelector(`m-quantity-input input`);
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      if (this.disableSelectedVariantDefault) {
        this.handleDisableSelectedVariantDefault();
      }

      // --- Require-explicit-selection mode (paired with disable_selected_variant_default) ---
      // When enabled, choosing one option must NOT auto-select the others: the shopper has to
      // pick EVERY option explicitly before a variant resolves and Add to Cart unlocks. We keep
      // the server fetch sending a full (auto-filled) combo so the chosen colour's image/price
      // still update, but the picker only ever shows the options the shopper actually chose and
      // the purchase stays blocked until the selection is complete.
      this.requireExplicitSelection = this.disableSelectedVariantDefault;
      // Map of optionIndex (string) -> chosen value string. Survives picker re-renders because it
      // lives on this (persistent) element, not the swapped-out picker DOM.
      this.explicitSelections = this.requireExplicitSelection
        ? this.readSelectedOptionIndexes()
        : new Map();

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      };

      this.initQuantityHandlers();

      if (!this.showFeaturedMedia) {
        if (this.currentVariant) this.updateMedia(this.currentVariant);
        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant: this.currentVariant, sectionId: this.sectionId },
        });
      }
    }

    getVariantGroupImagesData() {
      return (
        JSON.parse(
          this.querySelector('#variantGroup[type="application/json"]')
            .textContent
        ) || {}
      );
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      // Remember which options the shopper has explicitly chosen (source of truth for the gate).
      if (this.requireExplicitSelection) {
        this.recordExplicitSelection();
      }

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      const stickyExpanded = !!document.querySelector('.m-sticky-addtocart.is-expanded');
      const fromSticky = event.fromSticky === true || stickyExpanded;
      const preventFocus = fromSticky;

      // Aggressively capture scroll position if sticky is expanded
      let restoreScrollPos = null;
      if (preventFocus) {
        restoreScrollPos = window.scrollY;
      }

      // When a variant change swaps the gallery images (color), reset the view to the top of the
      // product so the sticky info column doesn't visually "jump" as the gallery height changes.
      //   Desktop : always.
      //   Mobile  : only when the change comes from the sticky ATC — NOT the on-page picker below
      //             the images.
      // (Only acted on when the images actually change; see renderProductInfo / handleVariantGroupImage.)
      const isMobile = window.matchMedia(MinimogTheme.config.mediaQueryMobile).matches;
      const scrollTopOnImages = !isMobile || fromSticky;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        preventFocus,
        restoreScrollPos,
        scrollTopOnImages,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
          : this.handleUpdateProductInfo(productUrl, this.viewMode, preventFocus),
      });
    }

    renderProductInfo({ requestUrl, targetId, callback, preventFocus, restoreScrollPos, scrollTopOnImages }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // Reset before the callback; handleVariantGroupImage() sets this true only when the
          // gallery images actually change (color change), not for size changes.
          this._variantImagesChanged = false;
          callback(html);

          if (scrollTopOnImages && this._variantImagesChanged) {
            // Gallery images changed: reset to the top of the product so the sticky info column
            // doesn't appear to jump. Instant (no animation) so it reads as part of the update.
            this.scrollProductToTop();
          } else if (restoreScrollPos !== null && restoreScrollPos !== undefined) {
            // Keep the shopper in place when interacting from the sticky (e.g. a size change).
            window.scrollTo(0, restoreScrollPos);
          } else if (!preventFocus) {
            // set focus to last clicked option value
            if (targetId) document.querySelector(`#${targetId}`)?.focus();
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    // Instantly bring the top of the product under the header (no smooth animation, so the reset
    // reads as part of the variant update rather than a visible scroll).
    scrollProductToTop() {
      const headerHeight = (window.MinimogSettings && MinimogSettings.headerHeight) || 0;
      const top = Math.max(0, window.scrollY + this.getBoundingClientRect().top - headerHeight);
      window.scrollTo({ top, left: 0, behavior: 'instant' });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl, viewMode, preventScroll) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variant = this.getSelectedVariant(html);

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);

        // Require-explicit: after the picker re-renders, strip any option the server auto-resolved
        // so only the shopper's explicit choices show, and learn whether every option is now chosen.
        const isComplete = this.requireExplicitSelection ? this.enforceExplicitSelection() : true;

        this.updateURL(productUrl, isComplete ? variant?.id : undefined);
        // Only carry a real variant id into the form when the selection is complete; otherwise the
        // form must not be submittable (no accidental "first size" added to cart).
        this.updateVariantInputs(isComplete ? variant?.id : '');

        if (!variant) {
          this.setUnavailable();
          return;
        }

        // Media/price still reflect the resolved variant so the chosen colour's image and (uniform)
        // price render even while the selection is incomplete.
        this.updateMedia(variant, preventScroll);
        this.updateTitleColorSuffix(variant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        // Selection not finished yet (e.g. colour chosen, size still empty): block purchase and tell
        // downstream listeners (sticky ATC, size sidebar) there is no committed variant.
        if (this.requireExplicitSelection && !isComplete) {
          // Hide per-variant stock/SKU (they'd otherwise show the auto-filled size's inventory,
          // which is misleading before a size is chosen) — same elements setUnavailable() hides.
          ['Inventory', 'Sku', 'Availability'].forEach((key) => {
            document.getElementById(`${key}-${this.sectionId}`)?.classList.add('m:hidden');
          });
          // Keep the button's normal label ("Add to shopping bag") — only disable it while the
          // selection is incomplete. (Per request: the Add-to-Cart label must never change.)
          this.toggleAddButton(true);
          this.currentVariant = null;
          MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
            data: { variant: null, sectionId: this.sectionId, html },
          });
          document.dispatchEvent(
            new CustomEvent('variant:changed', { detail: { variant: null } })
          );
          return;
        }

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.MinimogStrings.soldOut
        );

        this.currentVariant = variant;

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variant = this.getSelectedVariant(productInfo);
        productInfo.dataset.disableSelectedVariantDefault = false;

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
      };
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
    }

    updateVariantInputs(variantId) {
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updateOptionValues(html) {
      const variantPicker = html.querySelector('variant-picker');
      if (variantPicker) {
        HTMLUpdateUtility.viewTransition(this.variantPicker, variantPicker, this.preProcessHtmlCallbacks);
      }
    }

    setUnavailable() {
      // 1) disable Add to Cart (keep its label)
      const form = document.getElementById(`product-form-${this.sectionId}`);
      const btn = form?.querySelector('[name="add"]');
      if (btn) this.toggleAddButton(true);

      // 2) hide Inventory, SKU & Availability
      ['Inventory', 'Sku', 'Availability'].forEach(key => {
        const el = document.getElementById(`${key}-${this.sectionId}`);
        if (el) el.classList.add('m:hidden');
      });

      // 3) un-hide the price wrapper
      const priceEl = document.getElementById(`Price-${this.sectionId}`);
      if (!priceEl) return;
      priceEl.classList.remove('m:hidden');

      // 4) find the inner .m-price block (from product-prices.liquid)
      const priceWrapper = priceEl.querySelector('.m-price');
      if (!priceWrapper) {
        // fallback to plain text if markup is different
        priceEl.textContent = priceEl.dataset.priceMin;
        return;
      }

      // 5) replace only the regular-price amount (keeps all your classes!)
      const regularGroup = priceWrapper.querySelector('.m-price__regular');
      const amountEl = regularGroup?.querySelector('.m-price-item--regular');
      if (amountEl) {
        amountEl.textContent = priceEl.dataset.priceMin;
      }

      // 6) hide sale & unit-price if present
      priceWrapper.querySelector('.m-price__sale')?.classList.add('m:hidden');
      priceWrapper.querySelector('.m-price__unit-wrapper')?.classList.add('m:hidden');

      // 7) Remove any old "Start at" text node if present
      const firstNode = priceWrapper.childNodes[0];
      if (firstNode && firstNode.nodeType === Node.TEXT_NODE) {
        firstNode.textContent = '';
      }
    }




    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.innerHTML = window.MinimogStrings.addToCart;
      }

      if (!modifyClass) return;
    }

    updateMedia(variant, preventScroll) {
      if (!this.productMedia) return;
      if (this.enableVariantGroupImages && this.variantGroupImagesData.enable) {
        this.handleVariantGroupImage(variant);

        if (this.productMedia.slider) {
          this.productMedia.slider.updateSlides();
          this.productMedia.navSlider.updateSlides();

          this.productMedia.slider.slideTo(0);
          this.productMedia.navSlider.slideTo(0);

          this.productMedia.handleSlideChange();
        }
        if (this.productMedia.enableImageZoom) {
          this.productMedia.handlePhotoswipe();
        }
      } else {
        const setActiveMedia = () => {
          if (!preventScroll && typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.init();
            this.productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("[data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    // ---- Require-explicit-selection helpers (only used when disableSelectedVariantDefault) ----

    // Read the currently-selected value for each option group from the (server-rendered) picker.
    // Used to seed explicitSelections at load so a ?variant= deep link is treated as complete.
    readSelectedOptionIndexes() {
      const map = new Map();
      const picker = this.variantPicker;
      if (!picker) return map;
      picker.querySelectorAll('.m-product-option').forEach((group) => {
        const inner = group.querySelector('[data-option-index]');
        const idx = inner ? inner.dataset.optionIndex : null;
        if (idx == null) return;

        const radio = group.querySelector('input[type="radio"]:checked');
        if (radio) { map.set(String(idx), radio.value); return; }

        const sizeSelected = group.querySelector('.m-size-selector__selected');
        if (sizeSelected && sizeSelected.textContent.trim()) {
          map.set(String(idx), sizeSelected.textContent.trim());
          return;
        }

        const select = group.querySelector('select');
        if (select && select.selectedIndex >= 0) {
          const opt = select.options[select.selectedIndex];
          if (opt && !opt.disabled && opt.value) map.set(String(idx), opt.value);
        }
      });
      return map;
    }

    // Record the shopper's explicit choices by reading the picker's CURRENT selection and merging it
    // in. We can't rely on the change event's `target`: some flows (mobile sticky swatches) dispatch
    // `change` on the <variant-picker> element itself, not the individual input, so the option index
    // isn't derivable from the target. enforceExplicitSelection keeps the DOM in sync with these
    // choices, so a DOM read only ever reflects genuine, explicit selections (never auto-filled ones).
    recordExplicitSelection() {
      this.readSelectedOptionIndexes().forEach((value, idx) => {
        this.explicitSelections.set(idx, value);
      });
    }

    updateTitleColorSuffix(variant) {
      const suffixEl = this.querySelector('[data-title-color-suffix]');
      if (!suffixEl) return;
      const optionIndex = Number(suffixEl.dataset.colorOptionIndex);
      if (isNaN(optionIndex)) return;
      let value = variant && variant.options ? variant.options[optionIndex] : null;
      if (this.requireExplicitSelection) {
        value = this.explicitSelections.get(String(optionIndex)) || null;
      }
      suffixEl.textContent = value ? ` (${value})` : '';
    }

    // After a re-render: re-apply the shopper's explicit choices and clear every option they have
    // NOT chosen yet (the server auto-resolves a full variant, so those come back looking selected).
    // Returns true only when every option group has an explicit choice.
    enforceExplicitSelection() {
      const picker = this.variantPicker;
      if (!picker) return true;
      let complete = true;
      picker.querySelectorAll('.m-product-option').forEach((group) => {
        const inner = group.querySelector('[data-option-index]');
        const idx = inner ? String(inner.dataset.optionIndex) : null;
        if (idx == null) return;
        if (this.explicitSelections.has(idx)) {
          this.applyOptionGroup(group, this.explicitSelections.get(idx));
        } else {
          this.clearOptionGroup(group);
          complete = false;
        }
      });
      return complete;
    }

    // Reset one option group to its "nothing chosen" state (radios, plain selects, and the custom
    // size sidebar trigger + its drawer options).
    clearOptionGroup(group) {
      group.querySelectorAll('input[type="radio"]').forEach((r) => { r.checked = false; });

      const select = group.querySelector('select');
      if (select) select.selectedIndex = -1;

      const sizeTrigger = group.querySelector('[data-size-selected-value]');
      if (sizeTrigger) {
        sizeTrigger.innerHTML = this.dataset.variantOptionNoneText || '';
        sizeTrigger.closest('button')?.classList.add('m-size-selector__trigger--empty');
      }
      // Size drawer options are portalised to <body> (only the trigger stays in the group), and they
      // carry their own data-section-id — so match them directly rather than as drawer descendants.
      document
        .querySelectorAll(`[data-size-option][data-section-id="${this.dataset.sectionId}"]`)
        .forEach((opt) => {
          opt.classList.remove('is-selected');
          opt.removeAttribute('aria-current');
        });

      const label = group.querySelector('.option-label--selected');
      if (label) label.textContent = '';
    }

    // Ensure one option group visibly reflects the shopper's chosen value.
    applyOptionGroup(group, value) {
      group.querySelectorAll('input[type="radio"]').forEach((r) => {
        r.checked = r.value === value;
      });

      const select = group.querySelector('select:not(.m-size-sidebar__hidden-select)');
      if (select) select.value = value;

      const hiddenSelect = group.querySelector('.m-size-sidebar__hidden-select');
      if (hiddenSelect) hiddenSelect.value = value;

      const sizeTrigger = group.querySelector('[data-size-selected-value]');
      if (sizeTrigger) {
        const inner = group.querySelector('[data-option-name]');
        const optionName = (inner && inner.dataset.optionName) || 'Size';
        sizeTrigger.innerHTML =
          `<span class="m-size-selector__label">${optionName}:</span>` +
          `<span class="m-size-selector__selected">${value}</span>`;
        sizeTrigger.closest('button')?.classList.remove('m-size-selector__trigger--empty');
      }

      const label = group.querySelector('.option-label--selected');
      if (label) label.textContent = value;
    }

    handleVariantGroupImage(variant) {
      const selectedVariantData = this.variantGroupImagesData.mapping.find(
        (item) => Number(item.id) === variant.id
      );

      const selectedMedias = (initialItems) => {
        const selectedVariantMedias = Array.from(initialItems)
          .map((media) => {
            const index = selectedVariantData.media.indexOf(media.dataset.mediaId);
            const mediaType = media.dataset.mediaType;
            if (index !== -1 || mediaType !== 'image') {
              return media;
            }
            return null;
          })
          .filter(Boolean);

        if (selectedVariantMedias.length <= 0) {
          return Array.from(initialItems).map((media, index) => {
            media.dataset.index = index;

            media.removeAttribute('style'); // Avoid style of swipe slide item
            media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

            return media;
          });
        }

        const orderedMedias = Object.values(selectedVariantData.media)
          .map(id => selectedVariantMedias.find(media => media.dataset.mediaId === id))
          .filter(Boolean);

        const remainingMedias = selectedVariantMedias.filter(media => !Object.values(selectedVariantData.media).includes(media.dataset.mediaId));

        return Array.from([...orderedMedias, ...remainingMedias]).map((media, index) => {
          media.dataset.index = index;

          media.removeAttribute('style'); // Avoid style of swipe slide item
          media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

          return media;
        });
      }

      const mediaWrapper = this.productMedia.querySelector(".m-media-gallery__list");
      const thumbWrapper = this.productMedia.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");

      const layout = this.productMedia.dataset.layout;

      // Capture which media are shown so we can tell whether this variant actually changes the
      // gallery (color does; a size change within the same color does not). Drives scroll reset.
      const prevSignature = Array.from(mediaWrapper.children).map((m) => m.dataset.mediaId).join(',');

      mediaWrapper.innerHTML = "";
      thumbWrapper.innerHTML = "";

      selectedMedias(this.initialMedias).forEach((media, index) => {
        if (layout === "layout-2") {
          media.classList.remove("m-col-span-2");
          if (index % 3 === 0) {
            media.classList.add("m-col-span-2");
          }
        }
        mediaWrapper.append(media);
      });

      const newSignature = Array.from(mediaWrapper.children).map((m) => m.dataset.mediaId).join(',');
      this._variantImagesChanged = prevSignature !== newSignature;

      selectedMedias(this.initialThumbs).forEach((media) => {
        media.classList.toggle("swiper-slide-thumb-active", media.dataset.index === "0");
        thumbWrapper.append(media);
      });
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });
    }

    fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${currentVariantId}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })
    }
  }

  customElements.define("product-info", ProductInfo);
}